import type { Coordinate, Entity } from "game-common/models"
import { Mechanics } from "game-common/mechanics/mechanics"
import type { AnimatedSprite } from "pixi.js"

// Ground speed in pixels per second; walk cadence includes two successive 30% increases.
const REFERENCE_SPEED = 100
const WALK_CADENCE_MULTIPLIER = 1.69
// Cap the final cadence, including tuning, at three times the authored rate.
const MAX_RATE = 3
// Time constant of the measured ground speed: long enough to ride over uneven interpolation steps.
const SMOOTHING_MS = 200
// A stall longer than this, or a jump further than TELEPORT_DISTANCE, is not walking.
const MAX_SAMPLE_GAP_MS = 250
const TELEPORT_DISTANCE = 64
// A body easing to a stop never drops below this share of its gameplay speed, so a hop can't hang.
const MIN_GAMEPLAY_SHARE = 0.5

/**
 * Feet keep pace with the ground the body is DRAWN crossing. Gameplay speed overstates that for
 * NPCs (the server mover delivers ~71% of nominal, see MOVEMENT_EFFICIENCY; a follower is held to
 * its leader), so it seeds the cadence before the first measurement and optionally floors it after.
 */
export class WalkAnimationPace {
    private anchor?: { x: number; y: number; time: number }
    private groundSpeed?: number
    private rate = 1
    private settled = 0
    private authoredSpeeds = new WeakMap<AnimatedSprite, number>()

    // Sheet animations need a minimum cadence to finish a hop. Generated Ember gaits have
    // a known stride and must be allowed to follow even a very slow follower or enemy.
    constructor(private readonly minGameplayShare = MIN_GAMEPLAY_SHARE) {}

    update(entity: Entity, now = performance.now()): void {
        this.measure(entity.location, now)
        const gameplay = Mechanics.entity.speed.effectiveSpeed(entity)
        const nominal = Number.isFinite(gameplay) && gameplay > 0 ? gameplay : 0
        const speed = Math.max(this.groundSpeed ?? nominal, nominal * this.minGameplayShare)
        // A stopped entity leaves walk through the normal idle driver. Keep the cycle running
        // during that handoff rather than freezing an airborne frame.
        if (speed > 0) {
            this.rate = speed / REFERENCE_SPEED
            this.settled = speed
        }
    }

    /** Pixels per second the feet are keeping pace with; held through a stop, like the cadence. */
    get speed(): number {
        return this.settled
    }

    private measure({ x, y }: Coordinate, now: number): void {
        const anchor = this.anchor
        const elapsed = anchor ? now - anchor.time : Infinity
        const distance = anchor ? Math.hypot(x - anchor.x, y - anchor.y) : 0
        if (elapsed > MAX_SAMPLE_GAP_MS || distance > TELEPORT_DISTANCE) {
            // First sight, a stop, a deferred stretch or a teleport: measure afresh from here.
            this.anchor = { x, y, time: now }
            this.groundSpeed = undefined
            return
        }
        // No new position yet (interpolation waiting on the network): fold this frame into the
        // next step rather than read it as a stop.
        if (distance === 0 || elapsed <= 0) return

        const speed = (distance * 1000) / elapsed
        const blend = 1 - Math.exp(-elapsed / SMOOTHING_MS)
        this.groundSpeed = this.groundSpeed === undefined ? speed : this.groundSpeed + (speed - this.groundSpeed) * blend
        this.anchor = { x, y, time: now }
    }

    apply(action: string, animation?: AnimatedSprite): void {
        if (!action.startsWith("walk-") || !animation) return
        if (!this.authoredSpeeds.has(animation)) {
            this.authoredSpeeds.set(animation, animation.animationSpeed)
        }
        // Always use the authored value: repeated updates must not compound the multiplier.
        animation.animationSpeed = this.authoredSpeeds.get(animation)! * Math.min(MAX_RATE, this.rate * WALK_CADENCE_MULTIPLIER)
    }
}
